import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";

import { newValidate, newGetInitialValues, useAmsSelector } from "@awe-web/shared/lib";
import { Translation as T } from "@awe-web/shared/lib";
import { provisioningTypes } from "@awe-web/shared/lib";

import { B2B_LTR_SelectExtras } from "../../../routing/routes.js";
import { Row, Col } from "../../../components/grid.js";
import { useInitialize } from "../../../util/react.js";
import {
  ScreenSection,
  ScreenHeading,
  ScreenLoading
} from "../../../components/screen.js";
import { TypeArea } from "../../../components/layout.js";
import { getProvisioningTypeB2b } from "../../../rent_contracts/store.js";
import { Button, RequiredNotice } from "../../../components/form.js";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import { initialize, submit } from "./actions.js";
import SelectDriverAndProvisioningForm from "./select_driver_and_provisioning_form";

import "./select_driver_and_provisioning.scss";

const SCREEN_HEADING = "ALG0000065";
const BACK = "ALG0000030";
const NOTICE = "FSD0000209";
const PRICES_NOTICE = "ALG0000029";

function InternalSelectDriverAndProvisioning({ onSubmit }) {
  const provisioningType = useSelector((state) => getProvisioningTypeB2b(state));

  const initialValues = useAmsSelector((state) => {
    const driverFormInitializer = newGetInitialValues("personContractDriver");
    const driverInialValuesFactory = driverFormInitializer(state);
    const driverDefaultValues = driverInialValuesFactory({
      "Person:prefixKey": "",
      "Person:name": "",
      "Person:surname": "",
      "Address:email": "",
      "Address:phone": "",
      "employeeId": "",
      "costCenter": ""
    });

    const deliveryFormInitializer = newGetInitialValues("deliveryAddress07");
    const deliveryInialValuesFactory = deliveryFormInitializer(state);
    const deliveryDefaultValues = deliveryInialValuesFactory({
      "DeliveryAddress:surname": "",
      "DeliveryContactPerson:name": "",
      "DeliveryContactPerson:surname": "",
      "DeliveryAddress:street": "",
      "DeliveryAddress:building": "",
      "DeliveryAddress:zipCode": "",
      "DeliveryAddress:city": "",
      "DeliveryAddress:countryCode": "",
      "DeliveryContactPerson:phone": ""
    });

    const finalInitValues = { ...driverDefaultValues, ...deliveryDefaultValues, provisioningType };
    return finalInitValues;
  });

  const validateDelivery = useAmsSelector((state) => newValidate("deliveryAddress07")(state));
  const validateDriver = useAmsSelector((state) => newValidate("personContractDriver")(state));

  const validateForm = useCallback((values) => {
    const driverFormErrors = validateDriver(values);
    const deliveryFormErrors = values.provisioningType === provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS
      ? validateDelivery(values)
      : {};

    return {
      ...driverFormErrors,
      ...deliveryFormErrors
    };
  }, [validateDelivery, validateDriver]);

  return (
    <ScreenSection>
      <TypeArea>
        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <ScreenHeading><T code={SCREEN_HEADING} /></ScreenHeading>
          </Col>
        </Row>

        <Formik initialValues={initialValues} validate={validateForm} onSubmit={onSubmit}>
          {(formikProps) => (
            <SelectDriverAndProvisioningForm {...formikProps} />
          )}
        </Formik>

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <RequiredNotice className="b2b-ltr-select-driver-and-provisioning__required-notice"><T code={NOTICE} /></RequiredNotice>
            <div><T code={PRICES_NOTICE} /></div>
          </Col>
        </Row>
      </TypeArea>
    </ScreenSection>
  );
}

export default function B2B_LTR_SelectDriverAndProvisioning() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (formValues) => {
    const nextPath = await dispatch(submit(formValues));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2b-ltr-select-driver-and-provisioning">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2B" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2b-ltr-select-driver-and-provisioning__selected-rent-object"
            selectedBasicProperties="B2B"
            action={(
              <Button secondary back to={B2B_LTR_SelectExtras.path()} >
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <InternalSelectDriverAndProvisioning onSubmit={onSubmit} />
    </div>
  );
}
